import { Button } from '@/components/Button'
import { type ConsultationState } from '@/features/jobs/details/consultation-state'

export interface ConsultationActionsProps {
  state: ConsultationState
  canJoin: boolean
  isJoinPending: boolean
  onJoin: () => void
  isPickTimePending: boolean
  onPickTime: () => void
  onOpenChat: () => void
  canClose: boolean
  onComplete: () => void
  canDownloadCalendar: boolean
  isDownloadCalendarPending: boolean
  onDownloadCalendar: () => void
}

/**
 * The actions for where the call stands.
 */
export function ConsultationActions({
  state,
  canJoin,
  isJoinPending,
  onJoin,
  isPickTimePending,
  onPickTime,
  onOpenChat,
  canClose,
  onComplete,
  canDownloadCalendar,
  isDownloadCalendarPending,
  onDownloadCalendar,
}: ConsultationActionsProps) {
  const join = canJoin && (
    <Button
      label={state.phase === 'wrapUp' ? 'Start another call' : 'Join call'}
      size="small"
      variant={state.phase === 'wrapUp' ? 'default' : 'primary'}
      isDisabled={isJoinPending}
      isPending={isJoinPending}
      onPress={onJoin}
      testId="consultation-join-call-button"
    />
  )

  switch (state.phase) {
    case 'pick':
    case 'reschedule':
      return state.canPickTime ? (
        <Button
          label={
            state.phase === 'reschedule' ? 'Pick a new time' : 'Pick a time'
          }
          size="small"
          variant="primary"
          onPress={onPickTime}
          isDisabled={isPickTimePending}
          isPending={isPickTimePending}
          testId="book-consultation-button"
        />
      ) : null
    case 'arrange':
      return (
        <>
          <Button label="Open chat" size="small" onPress={onOpenChat} />
          {canClose && <CompleteButton onPress={onComplete} />}
          {join}
        </>
      )
    case 'booked':
      return (
        <>
          {canDownloadCalendar && (
            <Button
              label="Add to calendar"
              size="small"
              isDisabled={isDownloadCalendarPending}
              isPending={isDownloadCalendarPending}
              onPress={onDownloadCalendar}
              testId="consultation-download-calendar-button"
            />
          )}
          {join}
        </>
      )
    case 'live':
      return <>{join}</>
    case 'wrapUp':
      return (
        <>
          {join}
          {canClose && <CompleteButton onPress={onComplete} primary />}
        </>
      )
    default:
      return null
  }
}

interface CompleteButtonProps {
  onPress: () => void
  primary?: boolean
}

function CompleteButton({ onPress, primary }: CompleteButtonProps) {
  return (
    <Button
      label="Complete job"
      size="small"
      variant={primary ? 'primary' : 'default'}
      onPress={onPress}
      testId="consultation-complete-job-button"
    />
  )
}
